import React, { useEffect, useRef, useState } from 'react';
import { Mic, Square, Type, Waves } from 'lucide-react';
import {
  extensionForAudioMime,
  formatDuration,
  pickRecorderMime,
} from '../utils/boardChatAttachmentUtils';

function getSpeechRecognitionCtor() {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export function isSpeechRecognitionSupported() {
  return Boolean(getSpeechRecognitionCtor());
}

function joinTranscript(finalText, interimText) {
  return `${String(finalText || '').trim()} ${String(interimText || '').trim()}`
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Barra de gravação do microfone: Nota de voz | Transcrever.
 */
export function ComposerVoiceRecorder({
  active,
  busy,
  elapsedSec,
  onCancel,
  onVoiceNote,
  onTranscribe,
}) {
  if (!active) return null;
  return (
    <div className="bch-voice-bar" role="status" aria-live="polite">
      <span className={`bch-voice-pulse${busy ? ' is-busy' : ''}`} aria-hidden />
      <span className="bch-voice-timer">{formatDuration(elapsedSec)}</span>
      <span className="bch-voice-label">{busy ? 'Processando…' : 'Gravando…'}</span>
      <div className="bch-voice-actions">
        <button
          type="button"
          className="bch-voice-action"
          disabled={busy}
          onClick={onVoiceNote}
        >
          <Waves size={14} strokeWidth={1.9} />
          Nota de voz
        </button>
        <button
          type="button"
          className="bch-voice-action bch-voice-action--primary"
          disabled={busy}
          onClick={onTranscribe}
        >
          <Type size={14} strokeWidth={1.9} />
          Transcrever
        </button>
        <button
          type="button"
          className="bch-voice-cancel"
          disabled={busy}
          aria-label="Cancelar gravação"
          onClick={onCancel}
        >
          <Square size={12} strokeWidth={2.2} fill="currentColor" />
        </button>
      </div>
    </div>
  );
}

/** Hook de MediaRecorder + SpeechRecognition (pt-BR) para o composer. */
export function useComposerVoiceRecorder({ onError } = {}) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const mediaRef = useRef(null);
  const recordStreamRef = useRef(null);
  const recorderRef = useRef(null);
  const recognitionRef = useRef(null);
  const chunksRef = useRef([]);
  const mimeRef = useRef('');
  const transcriptRef = useRef('');
  const interimRef = useRef('');
  const recordingRef = useRef(false);
  const startedAtRef = useRef(0);
  const timerRef = useRef(null);
  const stopResolveRef = useRef(null);

  const clearTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const stopTracks = (stream) => {
    if (!stream) return;
    stream.getTracks().forEach((t) => {
      try {
        t.stop();
      } catch {
        /* ignore */
      }
    });
  };

  const cleanupStream = () => {
    stopTracks(recordStreamRef.current);
    recordStreamRef.current = null;
    stopTracks(mediaRef.current);
    mediaRef.current = null;
  };

  const stopSpeechRecognition = () => {
    const rec = recognitionRef.current;
    recognitionRef.current = null;
    if (!rec) return;
    try {
      rec.onend = null;
      rec.onerror = null;
      rec.onresult = null;
      rec.stop();
    } catch {
      try {
        rec.abort();
      } catch {
        /* ignore */
      }
    }
  };

  const startSpeechRecognition = () => {
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) return false;
    transcriptRef.current = '';
    interimRef.current = '';
    try {
      const recognition = new Ctor();
      recognition.lang = 'pt-BR';
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;
      recognition.onresult = (event) => {
        let interim = '';
        for (let i = event.resultIndex; i < event.results.length; i += 1) {
          const result = event.results[i];
          const piece = String(result?.[0]?.transcript || '');
          if (!piece) continue;
          if (result.isFinal) {
            transcriptRef.current = `${transcriptRef.current} ${piece}`
              .replace(/\s+/g, ' ')
              .trim();
          } else {
            interim += piece;
          }
        }
        interimRef.current = interim.trim();
      };
      recognition.onerror = (event) => {
        const code = event?.error;
        // Erros transitórios — reinício fica a cargo do onend.
        if (
          code === 'aborted' ||
          code === 'no-speech' ||
          code === 'audio-capture' ||
          code === 'network'
        ) {
          return;
        }
        if (code === 'not-allowed') {
          onError?.('Não foi possível acessar o microfone.');
        }
      };
      recognition.onend = () => {
        if (recordingRef.current && recognitionRef.current === recognition) {
          try {
            recognition.start();
          } catch {
            /* ignore */
          }
        }
      };
      recognitionRef.current = recognition;
      recognition.start();
      return true;
    } catch {
      recognitionRef.current = null;
      return false;
    }
  };

  const stopRecorder = () =>
    new Promise((resolve) => {
      const rec = recorderRef.current;
      if (!rec || rec.state === 'inactive') {
        resolve(null);
        return;
      }
      stopResolveRef.current = resolve;
      try {
        rec.stop();
      } catch {
        resolve(null);
      }
    });

  const cancel = async () => {
    recordingRef.current = false;
    clearTimer();
    setBusy(false);
    setRecording(false);
    setElapsedSec(0);
    stopSpeechRecognition();
    await stopRecorder();
    cleanupStream();
    chunksRef.current = [];
    transcriptRef.current = '';
    interimRef.current = '';
    recorderRef.current = null;
  };

  const start = async () => {
    if (recording || busy) return;
    if (!navigator?.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      onError?.('Não foi possível iniciar a gravação.');
      return;
    }
    try {
      // SpeechRecognition primeiro (captura própria) — reduz conflito com MediaRecorder.
      recordingRef.current = true;
      setRecording(true);
      startedAtRef.current = Date.now();
      setElapsedSec(0);
      clearTimer();
      timerRef.current = setInterval(() => {
        setElapsedSec(Math.floor((Date.now() - startedAtRef.current) / 1000));
      }, 250);

      const speechOk = startSpeechRecognition();
      await new Promise((r) => setTimeout(r, speechOk ? 180 : 0));

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      mediaRef.current = stream;

      // Clona tracks para o MediaRecorder — evita brigar com a captura da Speech API.
      const recordStream = new MediaStream(
        stream.getAudioTracks().map((track) => track.clone())
      );
      recordStreamRef.current = recordStream;

      const mime = pickRecorderMime();
      mimeRef.current = mime;
      const rec = mime
        ? new MediaRecorder(recordStream, { mimeType: mime })
        : new MediaRecorder(recordStream);
      mimeRef.current = rec.mimeType || mime || 'audio/webm';
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        const type = mimeRef.current || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type });
        const resolve = stopResolveRef.current;
        stopResolveRef.current = null;
        resolve?.(blob);
      };
      recorderRef.current = rec;
      rec.start(250);
    } catch {
      recordingRef.current = false;
      clearTimer();
      setRecording(false);
      stopSpeechRecognition();
      cleanupStream();
      onError?.('Não foi possível acessar o microfone.');
    }
  };

  const finish = async () => {
    setBusy(true);
    recordingRef.current = false;
    clearTimer();
    const durationSec = Math.max(
      0,
      Math.floor((Date.now() - (startedAtRef.current || Date.now())) / 1000)
    );
    stopSpeechRecognition();
    const blob = await stopRecorder();
    cleanupStream();
    setRecording(false);
    recorderRef.current = null;
    transcriptRef.current = '';
    interimRef.current = '';
    const mime = mimeRef.current || blob?.type || 'audio/webm';
    const ext = extensionForAudioMime(mime);
    const fileName = `nota-voz-${Date.now()}.${ext}`;
    setBusy(false);
    setElapsedSec(0);
    if (!blob || blob.size < 32) return null;
    return {
      blob,
      file: new File([blob], fileName, {
        type: mime.split(';')[0],
        lastModified: Date.now(),
      }),
      mime: mime.split(';')[0],
      fileName,
      durationSec,
    };
  };

  /** Encerra gravação e devolve texto pt-BR da Web Speech API. */
  const finishTranscript = async () => {
    setBusy(true);
    recordingRef.current = false;
    clearTimer();
    // Libera o MediaRecorder primeiro; mantém SR um pouco para fechar o último trecho.
    await stopRecorder();
    stopTracks(recordStreamRef.current);
    recordStreamRef.current = null;
    await new Promise((r) => setTimeout(r, 750));
    const text = joinTranscript(transcriptRef.current, interimRef.current);
    stopSpeechRecognition();
    stopTracks(mediaRef.current);
    mediaRef.current = null;
    setRecording(false);
    recorderRef.current = null;
    chunksRef.current = [];
    transcriptRef.current = '';
    interimRef.current = '';
    setBusy(false);
    setElapsedSec(0);
    return text;
  };

  useEffect(
    () => () => {
      recordingRef.current = false;
      clearTimer();
      stopSpeechRecognition();
      try {
        recorderRef.current?.stop?.();
      } catch {
        /* ignore */
      }
      cleanupStream();
    },
    []
  );

  return {
    recording,
    busy,
    elapsedSec,
    speechSupported: isSpeechRecognitionSupported(),
    start,
    cancel,
    finish,
    finishTranscript,
    toggle: async () => {
      if (recording) await cancel();
      else await start();
    },
  };
}

export function VoiceMicButton({ recording, onClick, tipProps, hideTip }) {
  return (
    <button
      type="button"
      className={`bch-composer-tool${recording ? ' is-recording' : ''}`}
      aria-label={recording ? 'Gravando áudio' : 'Fala para texto'}
      aria-pressed={recording}
      {...(tipProps?.(recording ? 'Gravando…' : 'Fala para texto', 'above') || {})}
      onClick={() => {
        hideTip?.();
        onClick?.();
      }}
    >
      <Mic size={16} strokeWidth={1.8} />
    </button>
  );
}
